'use client'
import { SectionHeader } from "@/components/ui/SectionHeader";
import { Card } from "@/components/ui/Card";
import { useState } from "react";
import { Button } from "@/components/ui/Button";
import Image from "next/image";
import advisorsData from "@/content/advisors.json";

export default function AboutPage(): React.ReactNode {
  const [expandedAdvisors, setExpandedAdvisors] = useState<Record<number, boolean>>({});

  const teamMembers = [
    {
      title: "Dr. Dario Villani",
      subtitle: "Co-founder & CEO",
      details: "Co-Founder of Qognitive, Inc. and is CEO and Co-Founder of Duality Group. Dario has managed multi-billion dollar portfolios within credit, interest rates, and commodities. Previously, he served as Global Head of Portfolio Strategy and Risk at Tudor Investment Corporation. He shared the 2016 Risk.net Buy-Side Quant of the Year Award, and has authored research papers in finance, theoretical physics, statistics and portfolio management. Dario holds a Ph.D. in Theoretical Physics from Salerno University and a Masters in Finance from Princeton University where he also taught a popular course in trading and risk management. Dario is currently a visiting professor in the Department of Mathematics at King's College London, an Advisory Board Member of FutureSum AI, Inc. and serves on the Board of Trustees of SLMath (formerly MSRI).",
      imageSrc: "/advisors/dario.png"
    },
    {
      title: "Dr. Kharen Musaelian",
      subtitle: "Co-founder, President and CSO",
      details: "Co-Founder of Qognitive, Inc. and is President / CIO and Co-Founder of Duality Group. Beginning in 1995, Kharen served in various portfolio management and investment research roles in the Proprietary Positioning Business of JP Morgan; managed multi-billion dollar investment portfolios and headed businesses at Merrill Lynch and BlueCrest Capital Management. In 2016, Kharen shared the Risk.net Buy-Side Quant of the Year Award. Kharen holds a PhD in theoretical physics from University of Rhode Island and MS / BS degrees in theoretical physics from the Moscow Institute of Physics and Technology (PhysTech).",
      imageSrc: "/advisors/kharen.png"
    },
  ];

  const advisors = advisorsData.advisors;

  return (
    <div className="container">
      <div className="px-4">
        <div className="border-b mb-12 pb-12">
          <SectionHeader variant="primary" size="xl" className="text-center mb-8">
            Our Team
          </SectionHeader>
          <div className="flex flex-row justify-center gap-12">
            {teamMembers.map((member, index) => (
              <Card
                key={index}
                title={member.title}
                subtitle={member.subtitle}
                details={member.details}
                imageSrc={member.imageSrc || undefined}
                imageAlt={`${member.title} portrait`}
                className="max-w-80 w-1/2"
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col">
          <div className="bg-gradient-to-t from-black/20 to-transparent py-8"></div>
          <div className="flex lg:flex-row flex-col bg-black/20 px-8">
            <div className="pr-4">
              <SectionHeader variant="primary" size="lg" className="lg:pr-8 text-nowrap" >
                Board of Advisors
              </SectionHeader>
            </div>
            <div className="lg:grow">
              {advisors.map((advisor, index) => (
                <div key={index} className="mb-6">
                  <div className="flex flex-row gap-4">
                    <div className="relative w-[65px] h-[65px]">
                      <Image
                        src={advisor.imageSrc}
                        alt={advisor.title}
                        fill
                        className="rounded-full object-cover"
                      />
                    </div>
                    <div className="flex flex-col">
                      <SectionHeader variant="subtle" size="lg" >{advisor.title}</SectionHeader>
                      <SectionHeader variant="subtle" size="md" >{advisor.subtitle}</SectionHeader>
                    </div>

                  </div>

                  <div className="relative pl-20">
                    <div
                      className={`text-s pt-2 text-gray-400 transition-all duration-300 ${expandedAdvisors[index] ? 'max-h-none' : 'max-h-[3.5rem] overflow-hidden'
                        }`}
                    >
                      {advisor.details}
                    </div>
                    <Button variant="link" size='sm' className="p-0"
                      onClick={() => setExpandedAdvisors(prev => ({ ...prev, [index]: !prev[index] }))}>
                      {expandedAdvisors[index] ? 'Show less' : 'Read more'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-gradient-to-t to-black/20 from-transparent py-8"></div>
        </div>

        <div className="my-12 border-t pt-12">
          <SectionHeader variant="subtle" size="lg" className="text-center pb-6" >
            Join our team designing tomorrow&apos;s AI
          </SectionHeader>
          <div className="flex justify-center items-center">
            <Button href="/careers" variant="default" size="lg">
              View Open Positions
            </Button>
          </div>
        </div>

      </div>
    </div>
  );
} 
